import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BillableIcon,
  Confirmation,
  Currency,
  DeleteConfirmation,
  Dropdown,
  FiltersBar,
  Icon,
  IconButton,
  InlineTooltip,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
  SingleSelectFilter,
  Tooltip,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useFeatures, useRowSelection } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { sortAlphabetically } from '~/utils';
import AvatarStack from '../AvatarStack';
import AddRolesModal from './AddClientRolesDialog';
import AddWorkspaceRolesDialog from './AddWorkspaceRolesDialog';
import ProjectRoleMembersModal from './ProjectRoleMembersModal';
import isSameRole from './isSameRole';
import BulkDelete from './BulkDelete';
import ProjectRoleForm from './ProjectRoleForm';
import SettingsActionsBanner from '../../SettingsActionsBanner';

export default function ProjectRolesTab({ project }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const features = useFeatures();
  const [data, setData] = useState(null);
  const [params, setParams] = useState({
    statusId: 'active',
  });

  // The whole list, unfiltered: the status filter narrows only what the table shows, while the add menu's
  // duplicate check reads every role — a duplicate hidden by the filter is still on the project.
  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).projectRoles().get();
    setData(data);
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = useMemo(() => {
    if (!data) return null;
    if (params.statusId === 'active') return data.filter((role) => role.isActive);
    if (params.statusId === 'inactive') return data.filter((role) => !role.isActive);
    return data;
  }, [data, params.statusId]);

  // `rows`, not `rows ?? []`: the hook already tolerates null, and a fresh [] on every render while the roles
  // load makes its allIds memo recompute, which re-runs its effect, which sets state, which renders again.
  const selection = useRowSelection({ rows });

  const [dialog, setDialog] = useState(null);
  const toast = useToast();

  const handleSaved = async () => {
    await fetchData();
    setDialog(null);
  };

  const handleShowForm = (projectRole = null) => {
    setDialog(
      <ProjectRoleForm
        projectId={project.id}
        projectRoleId={projectRole?.id}
        onSaved={handleSaved}
        onCancel={() => setDialog(null)}
      />,
    );
  };

  const handleEditMembers = (role) => {
    setDialog(
      <ProjectRoleMembersModal project={project} role={role} onSaved={handleSaved} onClose={() => setDialog(null)} />,
    );
  };

  const confirmation = useConfirmation();
  const handleDelete = async (entry) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Delete Project Role"
        resolve={async (result) => {
          if (result) {
            await api.www.workspaces(workspace.id).projectSettings(project.id).projectRoles(entry.id).delete();
            toast.success('Project Role deleted successfully.');
            selection.deselect(entry.id);
            await fetchData();
          }
          resolve(result);
        }}>
        Are you sure you want to delete this project role?
      </DeleteConfirmation>
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  const [clientRoles, setClientRoles] = useState([]);
  useEffect(() => {
    (async () => {
      if (!features.workspaceRoles) return;

      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .clientRoles({
          currency: project.currency,
          isActive: true,
          isBillable: project.isBillable === false ? false : undefined,
          size: 1000,
          clientId: project.client.id,
        });

      // A client role linked to a workspace role stores only the link — its name, practice, location,
      // discipline and rate live on the workspace role. Resolve once here so the picker, the add-all payload
      // and the duplicate check all read the same values; handing any of them the raw link adds a role from
      // its stale copies.
      setClientRoles(data.map((role) => (role.workspaceRole ? { ...role.workspaceRole, id: role.id } : role)));
    })();
  }, [api, workspace.id, project.id, project.currency, project.isBillable, project.client.id, features]);

  const [workspaceRoles, setWorkspaceRoles] = useState([]);
  useEffect(() => {
    (async () => {
      if (!features.workspaceRoles) return setWorkspaceRoles([]);

      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .workspaceRoles({
          currency: project.currency,
          isActive: true,
          isBillable: project.isBillable === false ? false : undefined,
          size: 1000,
        });

      setWorkspaceRoles(data);
    })();
  }, [api, workspace.id, project.id, project.currency, project.isBillable, features]);

  // Client and workspace roles carry the same fields, so both land on the project through one batch add.
  const addRoles = async (roles, label) => {
    try {
      await api.www
        .workspaces(workspace.id)
        .projectSettings(project.id)
        .projectRoles()
        .batch.add(
          roles.map((role) => ({
            name: role.name,
            practiceId: role.practice?.id,
            businessUnitId: role.businessUnit?.id,
            locationId: role.location?.id,
            // Workspace roles carry a discipline; dropping it here silently reclassified every role added.
            disciplineId: role.discipline?.id ?? role.disciplineId,
            rate: role.rate,
            isBillable: role.isBillable,
          })),
        );
      await fetchData();
      toast.success(`${label} added successfully.`);
      setDialog(null);
    } catch {
      toast.error(`Failed to add ${_.lowerCase(label)}.`);
    }
  };

  const handleAddClientRoles = async () => {
    setDialog(
      <AddRolesModal
        roles={clientRoles}
        title="Add Client Roles"
        onSubmit={(roles) => addRoles(roles, 'Client Roles')}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleAddWorkspaceRoles = async () => {
    setDialog(
      <AddWorkspaceRolesDialog
        workspaceRoles={workspaceRoles}
        projectRoles={data ?? []}
        onSubmit={(roles) => addRoles(roles, 'Workspace Roles')}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleActiveStatusChange = async (role) => {
    await confirmation.prompt((resolve) => (
      <Confirmation
        title={role.isActive ? 'Deactivate Project Role' : 'Activate Project Role'}
        resolve={async (result) => {
          try {
            await api.www
              .workspaces(workspace.id)
              .projectSettings(project.id)
              .projectRoles(role.id)
              .patch({ isActive: !role.isActive });
            toast.success(`Project Role ${role.isActive ? 'deactivated' : 'activated'} successfully.`);
          } catch (error) {
            toast.error(error.message);
          }
          await fetchData();
          resolve(result);
        }}>
        Are you sure you want to {role.isActive ? 'deactivate' : 'activate'} this project role?
      </Confirmation>
    ));
  };

  const handleUnassign = async (role, member) => {
    const pmr = role.members.find((pmr) => pmr.projectMember.member.id === member.id);
    if (!pmr) return;

    const confirmed = await confirmation.prompt((resolve) => (
      <Confirmation title="Unassign Member" resolve={resolve}>
        Are you sure you want to unassign {member.name} from the {role.name} role?
      </Confirmation>
    ));
    if (!confirmed) return;

    const projectMemberId = pmr.projectMember.id;
    // `data` is the unfiltered role list, so it carries the member's complete assignment set on this project.
    const roles = data
      .filter((r) => r.id !== role.id && r.members.some((m) => m.projectMember.id === projectMemberId))
      .map((r) => ({ projectRoleId: r.id }));

    try {
      await api.www.workspaces(workspace.id).projectSettings(project.id).members(projectMemberId).upsert({ roles });
      toast.success('Member unassigned successfully.');
    } catch (error) {
      toast.error(error.message);
    }
    await fetchData();
  };

  if (!data) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Roles</Page.Title>

        <Page.Actions>
          {project.permissions.edit && (
            <Dropdown align="right">
              {({ setIsOpen }) => {
                const unused = (roles) =>
                  roles.filter((role) => !data.some((pr) => isSameRole(role, pr, features.practices)));

                const actions = {
                  add: {
                    options: unused(clientRoles),

                    get tooltip() {
                      // Internal first: an internal project can't use client roles at all, so saying none exist
                      // would be wrong as well as unhelpful when the client actually has some.
                      if (project.client?.isInternal) return 'Client roles are not available for internal projects.';
                      if (clientRoles.length === 0) return 'No client roles exist.';
                      if (this.options.length === 0)
                        return "There are no available client-defined roles in the project's currency.";
                      return null;
                    },

                    get disabled() {
                      return !!this.tooltip;
                    },
                  },

                  workspace: {
                    options: unused(workspaceRoles),

                    get tooltip() {
                      if (workspaceRoles.length === 0) return 'No workspace roles exist.';
                      if (this.options.length === 0)
                        return "There are no available workspace-defined roles in the project's currency.";
                      return null;
                    },

                    get disabled() {
                      return !!this.tooltip;
                    },
                  },
                };

                return (
                  <>
                    <Dropdown.Trigger>
                      <IconButton
                        icon="plus"
                        onClick={() => setIsOpen(true)}
                        disabled={!project.permissions.edit}
                        tooltip="Add"
                      />
                    </Dropdown.Trigger>

                    {/* "New" is reserved for the top-level entities (client, project, opportunity); everything
                        added to a list inside one of them reads as "Add". */}
                    <Dropdown.Menu style={{ width: '15rem' }}>
                      <Dropdown.Item
                        onClick={() => {
                          setIsOpen(false);
                          handleShowForm(null);
                        }}>
                        Add Project Role
                      </Dropdown.Item>

                      {/* Client roles belong to the same rate-cards feature as workspace roles, so the whole
                          group is hidden when the feature is off. */}
                      {features.workspaceRoles && (
                        <Dropdown.Item
                          disabled={actions.add.disabled}
                          onClick={() => {
                            setIsOpen(false);
                            handleAddClientRoles();
                          }}>
                          Add Client Roles
                          {actions.add.tooltip && <InlineTooltip message={actions.add.tooltip} />}
                        </Dropdown.Item>
                      )}

                      {features.workspaceRoles && (
                        <Dropdown.Item
                          disabled={actions.workspace.disabled}
                          onClick={() => {
                            setIsOpen(false);
                            handleAddWorkspaceRoles();
                          }}>
                          Add Workspace Roles
                          {actions.workspace.tooltip && <InlineTooltip message={actions.workspace.tooltip} />}
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </>
                );
              }}
            </Dropdown>
          )}
        </Page.Actions>
      </Page.TabHeader>

      <Page.Filters>
        <FiltersBar>
          <SingleSelectFilter
            placeholder="Project Role Status"
            options={[
              { id: 'active', name: 'Active' },
              { id: 'inactive', name: 'Inactive' },
            ]}
            value={params.statusId}
            onChange={({ value }) => setParams((state) => ({ ...state, statusId: value }))}
          />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListViewSelection.Column
              testId="project_roles_bulk_checkbox"
              selection={selection}
              isVisible={project.permissions.edit}
              disabled={!rows.length}
            />
            <ListView.Column sticky minWidth="16rem">
              Project Role
            </ListView.Column>
            <ListView.Column minWidth="16rem">Assigned Members</ListView.Column>
            <ListView.Column minWidth="10rem" isVisible={features.practices}>
              Practice
            </ListView.Column>
            <ListView.Column minWidth="10rem" isVisible={features.businessUnits}>
              Business Unit
            </ListView.Column>
            <ListView.Column minWidth="10rem">Location</ListView.Column>
            <ListView.Column minWidth="10rem" isVisible={features.disciplines}>
              Discipline
            </ListView.Column>
            <ListView.Column
              isVisible={project.isBillable && project.permissions.viewRates}
              width="10rem"
              align="right">
              Bill Rate
            </ListView.Column>
            <ListViewActions.Column isVisible={project.permissions.edit} />
          </ListView.Header>

          <ListView.Body>
            {rows.map((role) => (
              <ListView.Row
                data-testid="row"
                key={role.id}
                isDisabled={!role.isActive}
                onClick={() => handleShowForm(role)}>
                <ListViewSelection.Cell id={role.id} selection={selection} testId="checkbox_project_role" />
                <ListView.Cell>{role.name}</ListView.Cell>
                <ListView.Cell>
                  <AvatarStack
                    members={sortAlphabetically(role.members, [
                      (pmr) => (pmr.projectMember.isActuallyActive ? 0 : 1),
                      'projectMember.member.name',
                    ]).map((pmr) => ({ ...pmr.projectMember.member, isInactive: !pmr.projectMember.isActuallyActive }))}
                    onRemove={project.permissions.edit ? (member) => handleUnassign(role, member) : undefined}
                  />
                  {project.isBillable && role.members.some((pmr) => !pmr.projectMember.member.isBillable) && (
                    <Tooltip
                      maxWidth="22rem"
                      placement="right"
                      message="A non-billable workspace member is assigned to this role and can't track time to this billable project.">
                      <Icon icon="exclamation-triangle" color={colors.warning} style={{ marginLeft: '0.5rem' }} />
                    </Tooltip>
                  )}
                </ListView.Cell>
                <ListView.Cell>{role.practice?.name}</ListView.Cell>
                <ListView.Cell>{role.businessUnit?.name}</ListView.Cell>
                <ListView.Cell>{role.location?.name}</ListView.Cell>
                <ListView.Cell>{role.discipline?.name}</ListView.Cell>
                <ListView.Cell>
                  {/* A role toggled to non-billable keeps its stored rate; showing it would be misleading. */}
                  <Currency
                    value={project.isBillable && role.isBillable ? role.rate : null}
                    currency={project.currency}
                    maximumFractionDigits={7}
                  />{' '}
                  <BillableIcon value={role.isBillable} style={{ marginLeft: '0.4rem' }} />
                </ListView.Cell>
                <ListViewActions isVisible={project.permissions.edit}>
                  <ListViewActions.Edit onClick={() => handleShowForm(role)} />
                  <hr />
                  <ListViewMenu>
                    {({ setIsOpen }) => (
                      <>
                        <ListViewMenu.Item
                          onClick={() => {
                            setIsOpen(false);
                            handleShowForm(role);
                          }}>
                          Edit
                        </ListViewMenu.Item>
                        <ListViewMenu.Item
                          onClick={() => {
                            setIsOpen(false);
                            handleEditMembers(role);
                          }}>
                          Edit Member Assignments
                        </ListViewMenu.Item>
                        <ListViewMenu.Item
                          onClick={() => {
                            setIsOpen(false);
                            handleActiveStatusChange(role);
                          }}>
                          {role.isActive ? 'Deactivate' : 'Activate'}
                        </ListViewMenu.Item>
                        <ListViewMenu.DeleteItem
                          tooltip="This project role is currently in use."
                          onCheckDependencies={async (workspace) =>
                            (await workspace.projectSettings(project.id).projectRoles(role.id).hasDependencies()).data
                          }
                          onClick={() => {
                            setIsOpen(false);
                            handleDelete(role);
                          }}>
                          Delete
                        </ListViewMenu.DeleteItem>
                      </>
                    )}
                  </ListViewMenu>
                </ListViewActions>
              </ListView.Row>
            ))}

            {rows.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={rows.length} label="Project Role" />
        </ListView>
      </Page.ListView>

      {dialog}

      {selection.any && (
        <SettingsActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <BulkActionMenu.Item
                onClick={() => {
                  setIsOpen(false);
                  handleBulkDelete();
                }}>
                Delete
              </BulkActionMenu.Item>
            )}
          </BulkActionMenu>
        </SettingsActionsBanner>
      )}
    </>
  );
}
